import { useState, useCallback } from "react";
import api from "@/lib/api";
import { parseCSV, downloadTemplate } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, FileText, CheckCircle2, AlertCircle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Results"];

export default function CSVImportDialog({ open, onOpenChange, entityType, onComplete }) {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [addQuantity, setAddQuantity] = useState(false);
  const [skipErrors, setSkipErrors] = useState(false);

  const reset = () => { setStep(0); setFile(null); setParsed({ headers: [], rows: [] }); setErrors([]); setResults(null); setImporting(false); };
  const handleClose = () => { reset(); onOpenChange(false); };

  const handleFileUpload = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith(".csv")) { toast.error("Only CSV files are accepted"); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = parseCSV(ev.target.result);
      setParsed(result);
      // Basic validation
      const errs = [];
      const reqFields = entityType === "products" ? ["product_name"] : ["company_name"];
      result.rows.forEach((row, i) => {
        reqFields.forEach((field) => {
          if (!(row[field] || "").trim()) errs.push({ row: i + 1, error: `${field} is required` });
        });
        if (row.email && !row.email.includes("@")) errs.push({ row: i + 1, error: `Invalid email: ${row.email}` });
        if (entityType === "products") {
          if (row.cost_price && isNaN(parseFloat(row.cost_price))) errs.push({ row: i + 1, error: "Cost price must be a number" });
          if (row.selling_price && isNaN(parseFloat(row.selling_price))) errs.push({ row: i + 1, error: "Selling price must be a number" });
        }
      });
      setErrors(errs);
      setStep(1);
    };
    reader.readAsText(f);
  }, [entityType]);

  const handleImport = async () => {
    setImporting(true);
    try {
      let rows = parsed.rows;
      if (skipErrors && errors.length > 0) {
        const errorRows = new Set(errors.map((e) => e.row));
        rows = rows.filter((_, i) => !errorRows.has(i + 1));
      }
      const payload = { rows };
      if (entityType === "products") payload.add_quantity = addQuantity;
      const res = await api.post(`/import/${entityType}`, payload);
      setResults(res.data);
      setStep(2);
      if (res.data.created > 0 || res.data.updated > 0) onComplete?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const validCount = parsed.rows.length - new Set(errors.map((e) => e.row)).size;
  const errorCount = new Set(errors.map((e) => e.row)).size;
  const label = entityType === "suppliers" ? "Suppliers" : entityType === "customers" ? "Customers" : "Products";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-xl">Import {label} from CSV</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? "bg-[#10B981] text-white" : i === step ? "bg-[#E63946] text-white" : "bg-gray-200 text-gray-500"
              }`}>{i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</div>
              <span className={`text-xs font-medium ${i === step ? "text-[#1F2937]" : "text-gray-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-200" />}
            </div>
          ))}
        </div>

        {/* Step 0: Upload */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <Button data-testid="download-template-button" variant="outline" onClick={() => downloadTemplate(entityType)} className="rounded-sm border-gray-300">
                <Download className="w-4 h-4 mr-1.5" /> Download CSV Template
              </Button>
            </div>
            <label data-testid="csv-upload-zone" className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-sm bg-gray-50 cursor-pointer hover:border-[#E63946] transition-colors">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500 font-medium">{file ? file.name : "Click to upload or drag CSV file"}</span>
              <span className="text-xs text-gray-400 mt-1">CSV files only</span>
              <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" data-testid="csv-file-input" />
            </label>
          </div>
        )}

        {/* Step 1: Preview */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-green-50 text-green-700 border-green-200 rounded-full text-xs">{validCount} valid</Badge>
                {errorCount > 0 && <Badge className="bg-red-50 text-red-700 border-red-200 rounded-full text-xs">{errorCount} errors</Badge>}
              </div>
              <span className="text-xs text-gray-500">{parsed.rows.length} rows total</span>
            </div>

            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 max-h-32 overflow-y-auto">
                {errors.slice(0, 10).map((e, i) => (
                  <p key={i} className="text-xs text-red-700 flex items-start gap-1.5 mb-1">
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> Row {e.row}: {e.error}
                  </p>
                ))}
                {errors.length > 10 && <p className="text-xs text-red-500">...and {errors.length - 10} more errors</p>}
              </div>
            )}

            {errors.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox id="skip-errors" checked={skipErrors} onCheckedChange={setSkipErrors} />
                <label htmlFor="skip-errors" className="text-sm text-gray-600 cursor-pointer">Skip error rows and import valid rows only</label>
              </div>
            )}

            {entityType === "products" && (
              <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quantity Update Mode</p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={!addQuantity} onChange={() => setAddQuantity(false)} className="accent-[#E63946]" />
                    <span className="text-sm text-gray-700">Replace quantities</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={addQuantity} onChange={() => setAddQuantity(true)} className="accent-[#E63946]" />
                    <span className="text-sm text-gray-700">Add to existing stock</span>
                  </label>
                </div>
              </div>
            )}

            {/* Preview table */}
            <div className="overflow-x-auto border border-gray-200 rounded-sm max-h-60">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-2 py-2 text-left font-semibold text-gray-500">#</th>
                    {parsed.headers.slice(0, 6).map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-gray-500 capitalize whitespace-nowrap">{h.replace(/_/g, " ")}</th>
                    ))}
                    {parsed.headers.length > 6 && <th className="px-2 py-2 text-gray-400">+{parsed.headers.length - 6} more</th>}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.slice(0, 10).map((row, i) => {
                    const hasError = errors.some((e) => e.row === i + 1);
                    return (
                      <tr key={i} className={hasError ? "bg-red-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                        {parsed.headers.slice(0, 6).map((h) => (
                          <td key={h} className="px-2 py-1.5 text-gray-700 max-w-[120px] truncate">{row[h] || "-"}</td>
                        ))}
                        {parsed.headers.length > 6 && <td className="px-2 py-1.5 text-gray-300">...</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {parsed.rows.length > 10 && <p className="text-xs text-gray-400 px-3 py-2 bg-gray-50 border-t">Showing first 10 of {parsed.rows.length} rows</p>}
            </div>
          </div>
        )}

        {/* Step 2: Results */}
        {step === 2 && results && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <p className="font-semibold text-green-800">Import completed!</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-heading font-bold text-green-700">{results.created}</p>
                  <p className="text-xs text-green-600">Created</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-blue-700">{results.updated}</p>
                  <p className="text-xs text-blue-600">Updated</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-red-700">{results.errors?.length || 0}</p>
                  <p className="text-xs text-red-600">Skipped</p>
                </div>
              </div>
            </div>
            {results.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-red-700 mb-2">Error Details:</p>
                {results.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600 mb-0.5">Row {e.row}: {e.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 0 && <Button variant="outline" onClick={handleClose} className="rounded-sm">Cancel</Button>}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => { setStep(0); setFile(null); }} className="rounded-sm">Back</Button>
              <Button data-testid="import-data-button" onClick={handleImport} disabled={importing || (validCount === 0 && !skipErrors)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
                {importing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing...</> : `Import ${skipErrors ? validCount : parsed.rows.length} ${label}`}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={reset} className="rounded-sm">Import More</Button>
              <Button onClick={handleClose} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
